<template>
  <div class="container">
    <header class="header pb-2.5">
      <div class="title px-4 font-normal">Door Summary</div>
      <div v-if="userName" class="user-name">{{ userName }}</div>
      <div class="flex flex-col px-4 pt-2.5">
        <div class="flex">
          <div
            v-for="tab in tabsRow1"
            :key="tab.key"
            :class="[
              'header-btn flex items-center justify-center mr-1.5 shrink-0',
              { active: activeTab.tabKey === tab.tabKey },
            ]"
            @click="tabAction(tab)"
          >
            {{ tab.key }}
          </div>
        </div>
        <div class="flex mt-1.5">
          <div
            v-for="tab in tabsRow2"
            :key="tab.key"
            :class="[
              'header-btn flex items-center justify-center mr-1.5 shrink-0',
              { active: activeTab.tabKey === tab.tabKey },
            ]"
            @click="tabAction(tab)"
          >
            {{ tab.key }}
          </div>
        </div>
      </div>
      <van-search
        class="mx-4 mt-2.5"
        v-model="storeName"
        placeholder="Search by project code, pos id, gate or store name."
        @search="onSearch"
      >
        <template #left-icon></template>
        <template #right-icon>
          <van-icon name="search" />
        </template>
      </van-search>
    </header>
    <div class="list-view">
      <van-pull-refresh
        v-model="refreshing"
        @refresh="onRefresh"
        pulling-text="Pull down to refresh"
        loosing-text="Release to refresh"
        loading-text="Loading..."
      >
        <van-empty v-if="!loading && listdata.length === 0" description="No Data" />
        <van-list
          v-if="loading || listdata.length > 0"
          v-model:loading="loading"
          loading-text="Loading..."
          :finished="finished"
          finished-text="No more"
          @load="onLoad"
        >
          <div
            v-for="i in listdata"
            :key="i.taskId || i.id || i.clcDoorCode"
            class="mx-4 mt-2 l-item py-4 px-4"
            @click="gotoDetail(i)"
          >
            <div class="flex justify-between gap-x-8">
              <div class="list-title">{{ i.doorName }}</div>
            </div>
            <div class="flex justify-between gap-x-1 mt-2">
              <div class="status">{{ i.currentGate }}</div>
              <div class="status-tag shrink-0">{{ i.doorStatus }}</div>
            </div>
            <div class="flex info-item mt-2 pb-2">
              <div class="flex-1 left-info pr-2">
                <div class="info-text info-top">{{ i.account }}</div>
                <div class="info-text">{{ i.clcProjectCode }}</div>
              </div>
              <div class="flex-1 shrink-0 pl-3">
                <div class="info-text info-top">
                  Exec: {{ formatDate(i.executionFlow, '{y}-{m}-{d}') }}, {{ i.fyQuarter }}
                </div>
                <div class="flex items-center info-text" v-show="i.remainingDays">
                  Remaining Time:
                  <div></div>
                  {{ i.remainingDays }} days
                </div>
              </div>
            </div>
          </div>
        </van-list>
      </van-pull-refresh>
    </div>
  </div>
</template>

<script setup lang="ts">
import { doorSummaryTab, type DoorSummaryTabItem } from '@/utils/config'
import { getDoorObserverList } from '@/api/history'
import type { IApprovalGroup, ITaskCount } from '@/types/approvals'
import { useRouter } from 'vue-router'
import { getCurrentUser } from '@/api/login'
import { formatDate } from '@/utils/utils'

const LAST_TAB_KEY = 'door_summary_last_tab_key'
const LAST_STORE_NAME = 'door_summary_last_store_name'
const initKey = sessionStorage.getItem(LAST_TAB_KEY)
const activeTab = ref<DoorSummaryTabItem>(
  doorSummaryTab.find((t) => t.key === initKey) || (doorSummaryTab[0] as DoorSummaryTabItem),
)
const storeName = ref(sessionStorage.getItem(LAST_STORE_NAME) || '')
const router = useRouter()
const listdata = ref<any[]>([])
const loading = ref(false)
const finished = ref(false)
const start = ref(1)
const length = ref(10)
const userName = ref('')
const refreshing = ref(false)

const tabsRow1 = computed(() => doorSummaryTab.filter((t) => t.row === 1))
const tabsRow2 = computed(() => doorSummaryTab.filter((t) => t.row === 2))

const tabAction = (tab: any) => {
  activeTab.value = tab
  sessionStorage.setItem(LAST_TAB_KEY, tab.key)
  resetAndLoad()
}

const onSearch = () => {
  sessionStorage.setItem(LAST_STORE_NAME, storeName.value)
  resetAndLoad()
}

const onLoad = async () => {
  loading.value = true
  try {
    const params: any = {
      processDefinitionKeys: activeTab.value.processDefinitionKeys || [],
      start: start.value,
      length: length.value,
      tabKey: String(activeTab.value.tabKey),
      storeNameProjectCodePosId: storeName.value,
    }
    const res = await getDoorObserverList(params)
    const items = Array.isArray(res?.data?.items) ? res.data.items : []
    if (start.value === 1) listdata.value = []
    listdata.value = [...listdata.value, ...items]
    if (!items || items.length < length.value) {
      finished.value = true
    }
    start.value += 1
  } catch (e) {
  } finally {
    loading.value = false
  }
}

const resetAndLoad = () => {
  finished.value = false
  start.value = 1
  listdata.value = []
  loading.value = false
  onLoad()
}

const onRefresh = async () => {
  finished.value = false
  start.value = 1
  listdata.value = []
  try {
    await onLoad()
  } finally {
    refreshing.value = false
  }
}

const fetchUser = async () => {
  try {
    if (sessionStorage.getItem('user_name')) {
      userName.value = sessionStorage.getItem('user_name') || ''
    } else {
      const result = await getCurrentUser()
      userName.value = String(result?.data?.user?.userName || '')
      sessionStorage.setItem('user_name', userName.value)
    }
  } catch {}
}

onMounted(() => {
  const isWeComEnv = () => /wxwork/i.test(navigator.userAgent)
  const hasToken = () => document.cookie.split('; ').some((s) => s.startsWith('Authorization='))
  const init = () => {
    fetchUser()
    resetAndLoad()
  }
  if (!isWeComEnv() || hasToken()) {
    init()
  } else {
    const handler = () => {
      window.removeEventListener('wecom-auth-done', handler)
      init()
    }
    window.addEventListener('wecom-auth-done', handler)
  }
})

const gotoDetail = (item: any) => {
  router.push({
    path: '/approvalsDetail',
    query: {
      batchId: item.batchId,
      taskId: item.taskId,
      clcDoorCode: item.clcDoorCode,
      clcProjectCode: item.clcProjectCode,
      storeNameLocal: item.doorName,
      currentTaskName: item.currentGate,
      tabIndex: activeTab.value.index,
      fromDoorSummary: '1',
    },
  })
}
</script>
<style lang="scss" scoped>
.container {
  width: 100%;
  height: 100%;
  font-family: 'Trade Gothic for Nike 365 BdCn';
}
.header {
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 9;
  background-color: #fff;
}
.user-name {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 12px;
  color: #333;
}
.gate-view {
  font-size: 14px;
  color: rgb(51, 51, 51);
}
.title {
  font-size: 18px;
  color: rgb(51, 51, 51);
  border: none;
}
.header-btn {
  height: 24px;
  padding-left: 10px;
  padding-right: 10px;
  font-size: 12px;
  color: rgb(29, 29, 29);
  border: 1px solid rgb(29, 29, 29);
  background-color: #fff;
  border-radius: 12px;
}
.header-btn.active {
  background-color: rgb(29, 29, 29);
  color: #fff;
}
:deep(.van-search__content) {
  font-size: 13px;
  background-color: #fff;
}
:deep(.van-search) {
  height: 28px;
  padding: 0;
  // border: 1px solid rgb(243, 244, 246);
  // box-shadow: rgba(255, 255, 255, 0) 0px 0px 1px;
  line-height: 28px;
  .van-search__field,
  .van-search__content {
    height: 28px;
    padding: 0;
    margin: 0;
    line-height: 28px;
  }
  .van-search__field {
    border: 2px solid rgb(243, 244, 246);
    box-shadow: rgba(255, 255, 255, 0) 0px 0px 1px;
    border-radius: 4px;
  }
  .van-icon-search {
    font-size: 16px;
  }
}
.checkbox {
  height: 13px;
  width: 13px;
  background-color: rgb(255, 255, 255);
  opacity: 1;
  border-width: 1px;
  border-color: rgb(119, 119, 119);
  border-style: solid;
  position: relative;
}
.checkbox.active {
  background-color: rgb(119, 119, 119);
}
// 选中状态
.select-item.active::after,
.checkbox.active::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  background-image: url('@/assets/gc-bi/icon/SVG/check.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}
// 部分选中
.checkbox.partial::after {
  content: '—';
  color: #fff;
  font-size: 12px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.checkbox-text {
  font-size: 13px;
  text-align: left;
  color: rgb(127, 127, 127);
  font-weight: bold;
}
.footer-view {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50px;
  background-color: #fff;
  border-top: 1px solid rgb(243, 244, 246);
  padding-bottom: env(safe-area-inset-bottom);
  z-index: 9;
}
.footer-btn {
  width: 120px;
  height: 48px;
  font-size: 16px;
  color: rgb(255, 255, 255);
  font-weight: bold;
  background-color: rgb(25, 190, 107);
  border-radius: 4.5px;
  transition: unset;
}
.list-view {
  height: 100vh;
  overflow-x: auto;
  scrollbar-width: none;
  padding-top: 140px;
  padding-bottom: calc(env(safe-area-inset-bottom) + var(--van-tabbar-height));
  box-shadow: -8px 0 12px rgba(0, 0, 0, 0.15);
}
.gate-view {
  font-size: 13px;
  color: rgb(85, 85, 85);
  font-weight: bold;
}
.l-item {
  background-color: rgb(255, 255, 255);
  filter: drop-shadow(rgba(0, 0, 0, 0.15) 0px 3px 6px);
  border-radius: 12.5px;
  transition: unset;
  position: relative;
}
.select-item {
  width: 18px;
  height: 18px;
  border-radius: 9px;
  border: 1px solid rgb(119, 119, 119);
  background-color: #fff;
  position: relative;
}
.select-item.active {
  background-color: rgb(245, 155, 34);
}
.list-title {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
  font-weight: bold;
  line-height: 16px;
}
.status {
  font-size: 12px;
  line-height: 14px;
  color: rgb(1, 167, 240);
}
.status-tag {
  height: 16px;
  font-size: 11px;
  line-height: 16px;
  color: rgb(234, 88, 12);
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 8px;
  border: 1px solid rgb(243, 244, 246);
}
.info-item {
  border-top: 1px solid rgb(243, 244, 246);
}
.info-text {
  font-size: 12px;
  font-weight: bold;
  color: rgb(127, 127, 127);
}

.info-top {
  color: rgb(170, 170, 170);
}
.left-info {
  border-right: 1px solid rgb(243, 244, 246);
}
</style>
